import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react"
import { releasePendingAgentImage, type PendingAgentImage } from "@/components/agent/agent-image-attach"
import { agentComposerDraftKey, type AgentComposerDraftKeyParts } from "@/lib/agent-composer-draft"
import {
  readAgentComposerImages,
  touchAgentComposerImages,
  writeAgentComposerImages,
  type WriteAgentComposerImagesResult,
} from "@/lib/agent-composer-images"

type ComposerImagesState = {
  key: string
  images: PendingAgentImage[]
  hydrated: boolean
}

type UseAgentComposerImagesOptions = {
  onQuotaExceeded?: () => void
}

/**
 * Черновик картинок композера на ту же беседу, что и текст.
 * Пока `ready` ложный, вставку лучше не принимать: позднее восстановление затрёт новый файл.
 */
export function useAgentComposerImages(
  { projectId, mode, contextType, contextId }: AgentComposerDraftKeyParts,
  options?: UseAgentComposerImagesOptions,
) {
  const key = useMemo(
    () => agentComposerDraftKey({ projectId, mode, contextType, contextId }),
    [projectId, mode, contextType, contextId],
  )
  const [state, setState] = useState<ComposerImagesState>(() => ({ key, images: [], hydrated: false }))
  const pendingImagesRef = useRef<PendingAgentImage[]>([])
  const flushRef = useRef<{ key: string; images: PendingAgentImage[]; generation: number } | null>(null)
  const userEditedRef = useRef(false)
  const hydratedImagesRef = useRef<PendingAgentImage[] | null>(null)
  const activeKeyRef = useRef(key)
  const hydratedRef = useRef(false)
  const writeGenRef = useRef(0)
  const toastedQuotaRef = useRef(new Set<string>())
  const onQuotaExceededRef = useRef(options?.onQuotaExceeded)
  const readyRef = useRef<Promise<void>>(Promise.resolve())
  const epochRef = useRef(0)
  onQuotaExceededRef.current = options?.onQuotaExceeded

  if (state.key !== key) {
    epochRef.current += 1
    if (state.hydrated) {
      const generation = writeGenRef.current + 1
      writeGenRef.current = generation
      flushRef.current = { key: state.key, images: state.images, generation }
    }
    userEditedRef.current = false
    hydratedImagesRef.current = null
    setState({ key, images: [], hydrated: false })
  }

  const pendingImages = state.key === key ? state.images : []
  pendingImagesRef.current = pendingImages
  activeKeyRef.current = key
  hydratedRef.current = state.hydrated && state.key === key

  const noteQuota = useCallback((generation: number, result: WriteAgentComposerImagesResult) => {
    if (writeGenRef.current !== generation || !result.quotaExceeded) return
    const unseen = result.omittedUploadKeys.filter((id) => !toastedQuotaRef.current.has(id))
    if (unseen.length === 0) return
    unseen.forEach((id) => toastedQuotaRef.current.add(id))
    onQuotaExceededRef.current?.()
  }, [])
  const noteQuotaRef = useRef(noteQuota)
  noteQuotaRef.current = noteQuota

  const setPendingImages = useCallback<Dispatch<SetStateAction<PendingAgentImage[]>>>((action) => {
    userEditedRef.current = true
    hydratedImagesRef.current = null
    setState((prev) => {
      const next = typeof action === "function" ? action(prev.images) : action
      pendingImagesRef.current = next
      return { ...prev, images: next }
    })
  }, [])

  useEffect(() => {
    if (!state.hydrated || state.key !== key) return
    if (!userEditedRef.current && state.images.length === 0) return

    const generation = ++writeGenRef.current
    const images = state.images
    const restored = !userEditedRef.current && images === hydratedImagesRef.current
    const write = restored
      ? touchAgentComposerImages(key, images).then(() => ({ omittedUploadKeys: [], quotaExceeded: false }))
      : writeAgentComposerImages(key, images)
    void write.then(
      (result) => noteQuota(generation, result),
      () => undefined,
    )
  }, [key, noteQuota, state.hydrated, state.images, state.key])

  useEffect(() => {
    let cancelled = false
    let resolveReady = () => {}
    readyRef.current = new Promise<void>((resolve) => {
      resolveReady = resolve
    })

    void readAgentComposerImages(key)
      .then((images) => {
        if (cancelled || userEditedRef.current) {
          images.forEach(releasePendingAgentImage)
          if (!cancelled) setState((prev) => (prev.key === key ? { ...prev, hydrated: true } : prev))
          return
        }
        hydratedImagesRef.current = images
        pendingImagesRef.current = images
        setState((prev) => (prev.key === key ? { key, images, hydrated: true } : prev))
      })
      .finally(() => {
        resolveReady()
      })

    return () => {
      cancelled = true
      resolveReady()
      const flush = flushRef.current
      flushRef.current = null
      if (!flush) return
      void writeAgentComposerImages(flush.key, flush.images).then(
        (result) => noteQuotaRef.current(flush.generation, result),
        () => undefined,
      )
      flush.images.forEach(releasePendingAgentImage)
    }
  }, [key])

  useEffect(() => {
    const flushCurrent = () => {
      // Незатронутый черновик уже на диске. Повторная запись на каждом refresh копирует байты зря
      // и может затереть только что прочитанный манифест пустым списком.
      if (!hydratedRef.current || !userEditedRef.current) return
      void writeAgentComposerImages(activeKeyRef.current, pendingImagesRef.current)
    }
    window.addEventListener("pagehide", flushCurrent)
    return () => {
      window.removeEventListener("pagehide", flushCurrent)
      flushCurrent()
    }
  }, [])

  const whenReady = useCallback(() => readyRef.current, [])

  return {
    pendingImages,
    setPendingImages,
    pendingImagesRef,
    epochRef,
    ready: state.hydrated && state.key === key,
    whenReady,
  }
}
